import React, { useState } from "react";

import { faCheck, faPencil } from "@fortawesome/free-solid-svg-icons";
import { faPlus, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { Divider, Grid } from "@material-ui/core";
import { FieldArray, useFormikContext } from "formik";
import { isObject } from "lodash";
import { useSelector } from "react-redux";
import { RRule } from "rrule";
import { v4 as uuidv4 } from "uuid";

import { SlimButton } from "components/Button";
import { ButtonIcon } from "components/Button/FontAwesomeButton";
import { SaleSubTypeField } from "components/Form/Fields/SaleFilter/SaleSubTypeField";
import { SaleTypeField } from "components/Form/Fields/SaleFilter/SaleTypeField";
import { SpeciesField } from "components/Form/Fields/SaleFilter/SpeciesField";
import {
  Error,
  Label,
  withArrayNamespace,
  withNamespace,
} from "components/Form/FormikControls";
import { Column, SpaceBetweenRow } from "components/Layout";
import { SmallText } from "components/Text";

import { getDefaultBillingSettings } from "selectors";

import { useFieldMeta, useFieldValue } from "hooks";

import { TermType } from "./constants";
import { TradingTermInputs } from "./rruleInputs";
import { getTermSummary } from "./utils";

const generateDefaultTerm = billingSettingsId => ({
  id: uuidv4(),
  saleType: null,
  speciesId: null,
  saleSubTypeId: null,
  buyerTerms: 0,
  vendorTerms: 0,
  buyerConfig: { freq: RRule.DAILY },
  vendorConfig: { freq: RRule.DAILY },
  buyerScheduleDueRule: null,
  vendorScheduleDueRule: null,
  billingSettingsId,
});

function TermsSection({ ns, termType, setFieldValue, values, name, index }) {
  const termLabel = termType === TermType.BUYER ? "Buyer" : "Vendor";
  const { errors } = useFormikContext();
  const fieldError =
    errors &&
    errors?.[name]?.[index] &&
    errors?.[name]?.[index]?.[
      termType === TermType.BUYER ? "buyerTerms" : "vendorTerms"
    ];
  return (
    <Grid container spacing={1} alignItems="center">
      <Grid item xs={12}>
        <Label>{termLabel} Terms Configuration</Label>
      </Grid>

      <Grid item xs={12} md={12}>
        <TradingTermInputs
          namespace={ns}
          termType={termType}
          values={values}
          name={name}
          index={index}
          setFieldValue={setFieldValue}
        />
        {fieldError && (
          <Error>
            {typeof fieldError === "string"
              ? fieldError
              : JSON.stringify(fieldError)}
          </Error>
        )}
      </Grid>
    </Grid>
  );
}

function TradingTerm(props) {
  const { index, name, namespace, removeRow, tradingTerm } = props;
  const ns = withArrayNamespace(withNamespace(namespace, name), index);
  const { setFieldValue, values } = useFormikContext();
  const { error } = useFieldMeta(ns);
  // if there are errors in either of the terms fields, we should not let the user toggle the terms section
  const hasError = error && Object.keys(error).length > 0;
  // If we don't have buyer or vendor terms, we should show the terms section by default as they will need to be configured
  const [showTerms, setShowTerms] = useState(false);

  return (
    <Grid container spacing={1} alignItems="center">
      <Grid item xs={6} md={4}>
        <SaleTypeField namespace={ns} required />
      </Grid>
      <Grid item xs={6} md={4}>
        <SpeciesField namespace={ns} required />
      </Grid>
      <Grid item xs={6} md={3}>
        <SaleSubTypeField namespace={ns} />
      </Grid>

      <Grid item xs={2} md={1} spacing={2} container justifyContent="flex-end">
        <Grid item xs={6} md={6}>
          <SlimButton
            onClick={() => (!hasError ? setShowTerms(v => !v) : null)}
            type="button"
            backgroundColor="primary"
            disabled={hasError}
          >
            <ButtonIcon icon={showTerms ? faCheck : faPencil} />
          </SlimButton>
        </Grid>
        <Grid item xs={6} md={6}>
          <SlimButton
            onClick={() => removeRow(index)}
            backgroundColor="deleteRed"
            type="button"
          >
            <ButtonIcon icon={faTrash} />
          </SlimButton>
        </Grid>
      </Grid>

      {showTerms ? (
        <Grid item container spacing={3} xs={12} md={12}>
          <Grid item xs={6} md={6}>
            <TermsSection
              ns={ns}
              termType={TermType.BUYER}
              setFieldValue={setFieldValue}
              values={values}
              name={name}
              index={index}
            />
          </Grid>
          <Grid item xs={6} md={6}>
            <TermsSection
              ns={ns}
              termType={TermType.VENDOR}
              setFieldValue={setFieldValue}
              values={values}
              name={name}
              index={index}
            />
          </Grid>
        </Grid>
      ) : (
        <Grid item container xs={12} md={12}>
          <Grid item xs={6} md={6}>
            <SmallText justifyContent="center">
              <strong>Buyer Terms: &nbsp;</strong>
              {getTermSummary(tradingTerm, TermType.BUYER)}
            </SmallText>
          </Grid>
          <Grid item xs={6} md={6}>
            <SmallText justifyContent="center">
              <strong>Vendor Terms: &nbsp;</strong>
              {getTermSummary(tradingTerm, TermType.VENDOR)}
            </SmallText>
          </Grid>
        </Grid>
      )}

      {error?.rowError && (
        <Grid item xs={12}>
          <Error>{error.rowError}</Error>
        </Grid>
      )}

      <Grid xs={12} item>
        <Divider />
        <SpaceBetweenRow />
      </Grid>
    </Grid>
  );
}

export function TradingTerms(props) {
  const { namespace = "", name = "tradingTerms", tooltip } = props;

  const tradingTerms = useFieldValue(withNamespace(namespace, name));
  const { errors } = useFormikContext();
  const error = errors[withNamespace(namespace, name)];

  const billingSettingsId = useSelector(getDefaultBillingSettings)?.id;

  if (!billingSettingsId) {
    return null;
  }

  return (
    <Column fullWidth fullHeight>
      <Label tooltip={tooltip} />
      <Column fullWidth fullHeight paddingHorizontal={3}>
        <FieldArray name={withNamespace(namespace, name)}>
          {arrayHelpers => {
            const addNew = () => {
              arrayHelpers.push(generateDefaultTerm(billingSettingsId));
            };

            return (
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <SlimButton onClick={addNew} type="button">
                    <ButtonIcon icon={faPlus} />
                    &nbsp;New Trading Terms
                  </SlimButton>
                </Grid>

                {!isObject(error) && error && (
                  <Grid item xs={12}>
                    <Error>{error}</Error>
                  </Grid>
                )}

                {tradingTerms.length > 0 && (
                  <>
                    <Grid item xs={6} md={4}>
                      <Label>Sale Type</Label>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Label>Species</Label>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Label>Sale Sub Type</Label>
                    </Grid>
                    <Grid item xs={1} md={1}>
                      &nbsp;
                    </Grid>

                    <Grid xs={12} item>
                      <Divider />
                    </Grid>

                    {tradingTerms.map((tradingTerm, idx) => (
                      <TradingTerm
                        key={idx}
                        index={idx}
                        removeRow={arrayHelpers.remove}
                        name={name}
                        namespace={namespace}
                        tradingTerm={tradingTerm}
                      />
                    ))}
                  </>
                )}
              </Grid>
            );
          }}
        </FieldArray>
      </Column>
    </Column>
  );
}
